import React, { useEffect, useState } from "react";
import AdminLeftMenu from "../../Navbar/AdminLeftMenu";
import AdminHeader from "../../Header/AdminHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faPlus,
  faSearch,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { CrossOriginApi } from "../../Dashboard/api/Api";
import styles from './CrossOrigin.module.css'

const CrossOrigin = () => {
  const lStorage =
    JSON.parse(localStorage.getItem("login")) ||
    JSON.parse(localStorage.getItem("admin")) ||
    JSON.parse(localStorage.getItem("emp")) ||
    JSON.parse(localStorage.getItem("reseller"));

  const userId = lStorage?.user?.userId;
  const userToken = lStorage?.token;

  const [domains, setDomains] = useState([]);
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [error, setError] = useState({ show: false, message: "", bg: "" });
  const [success, setSuccess] = useState({ show: false, message: "" });
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const domainsPerPage = 10;

  // Popups
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showInsertPopup, setShowInsertPopup] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [editedDomain, setEditedDomain] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [newStatus, setNewStatus] = useState(1);
  const [newIp, setNewIp] = useState("");

  const [editedStatus, setEditedStatus] = useState(1);
  const [editedIp, setEditedIp] = useState("");


  const showError = (msg) => {
    setError({ show: true, message: msg, bg: "alert alert-danger" });
    setTimeout(() => setError({ show: false }), 3000);
  };

  const showSuccess = (msg) => {
    setSuccess({ show: true, message: msg });
    setTimeout(() => setSuccess({ show: false, message: "" }), 2500);
  };

  // ✅ Fetch all domains once
  const fetchDomains = async () => {
    if (!userId || !userToken) {
      showError("Missing user credentials. Please login again.");
      return;
    }

    try {
      const payload = {
        method: "ListDomain",
        user_id: userId,
        user_token: userToken,
        user_type: "admin",
        filter_type: "all",
      };

      const { data } = await CrossOriginApi(payload);

      if (data.success) {
        setDomains(data.data || []);
        setFilteredDomains(data.data || []);
        setCurrentPage(1);
      } else {
        setDomains([]);
        setFilteredDomains([]);
        showError(data.message || "Failed to fetch domains");
      }
    } catch {
      showError("Error fetching domains");
    }
  };

  // ✅ Local Search (like DataTable)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredDomains(domains);
    } else {
      const query = searchQuery.toLowerCase().trim();
      const results = domains.filter((d) =>
        d.domain?.toLowerCase().includes(query)
      );
      setFilteredDomains(results);
      setCurrentPage(1);
    }
  }, [searchQuery, domains]);

  // Pagination logic
  const indexOfLastDomain = currentPage * domainsPerPage;
  const indexOfFirstDomain = indexOfLastDomain - domainsPerPage;
  const currentDomains = filteredDomains.slice(
    indexOfFirstDomain,
    indexOfLastDomain
  );
  const totalPages = Math.ceil(filteredDomains.length / domainsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // Insert new domain
  const handleInsert = async () => {
    const domainToAdd = newDomain.trim();

    if (!domainToAdd) {
      showError("Please enter a domain name");
      return;
    }

    // ✅ Local duplicate check (case-insensitive)
    const existsLocally = domains.some(
      (d) => String(d.domain || "").toLowerCase() === domainToAdd.toLowerCase()
    );
    if (existsLocally) {
      showError("Domain already exists");
      // 👇 Close popup and reset input
      setShowInsertPopup(false);
      setNewDomain("");
      return;
    }

    if (!userId || !userToken) {
      showError("Missing user credentials. Please login again.");
      return;
    }

    try {
      const payload = {
        user_id: userId,
        user_token: userToken,
        user_type: "admin",
        domain: domainToAdd,
        method: "insertDomain",
        ip: newIp || null,
      };

      const { data } = await CrossOriginApi(payload);

      if (data && data.success) {
        setShowInsertPopup(false);
        setNewDomain("");
        setNewStatus(1);
        setNewIp("");
        fetchDomains();
        showSuccess("Domain added successfully!");
      } else {
        // ✅ Handle specific API message if domain already exists
        const apiMessage =
          (data && (data.message || data.msg || data.error)) ||
          "Failed to insert domain";

        if (/exists|already|duplicate|taken/i.test(apiMessage)) {
          showError("Domain already exists");
          // 👇 Close popup and reset input
          setShowInsertPopup(false);
          setNewDomain("");
        } else {
          showError(apiMessage);
        }
      }
    } catch (err) {
      const apiErrMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "";

      if (/exists|already|duplicate|taken/i.test(apiErrMsg)) {
        showError("Domain already exists");
        // 👇 Close popup and reset input
        setShowInsertPopup(false);
        setNewDomain("");
      } else if (apiErrMsg) {
        showError(apiErrMsg);
      } else {
        showError(`${err.response?.data?.message}` || "Something went wrong while adding domain");
      }
    }
  };

  // Update Domain
  const handleEdit = async () => {
    if (!editedDomain.trim()) {
      showError("Domain name cannot be empty");
      return;
    }

    if (!userId || !userToken) {
      showError("Missing user credentials. Please login again.");
      return;
    }

    try {
      const payload = {
        user_id: userId,
        user_token: userToken,
        user_type: "admin",
        domain: editedDomain.trim(),
        id: selectedDomain.id,
        status: editedStatus,
        ip: editedIp || null,
        method: "updateDomain",
      };

      const { data } = await CrossOriginApi(payload);

      if (data.success) {
        setShowEditPopup(false);
        fetchDomains();
        showSuccess("Domain updated successfully!");
      } else {
        showError(data.message || "Failed to update domain");
      }
    } catch (err) {
      showError(`${err.response?.data?.message}` || "Something went wrong while updating domain");
    }
  };

  // Delete Domain
  const handleDelete = async () => {
    if (!userId || !userToken) {
      showError("Missing user credentials. Please login again.");
      return;
    }

    try {
      const payload = {
        user_id: userId,
        user_token: userToken,
        user_type: "admin",
        id: selectedDomain.id,
        method: "DeleteDomain",
      };

      const { data } = await CrossOriginApi(payload);

      if (data.success) {
        setShowDeletePopup(false);
        fetchDomains();
        showSuccess("Domain deleted successfully!");
      } else {
        showError(data.message || "Failed to delete domain");
      }
    } catch (err) {
      showError(`${err.response?.data?.message}` || "Something went wrong while deleting domain");
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchDomains();
  }, []);

  return (
    <div className="wrapper">
      <AdminLeftMenu />
      <div className="main">
        <AdminHeader />
        <main className="content">
          {/* ✅ Success Toast */}
          {success.show && <div className={styles.successToast}>{success.message}</div>}

          <div className="card">
            <div className="card-body">
              <div className="container-fluid p-0">
                {/* Header Section */}
                <div className={`d-flex justify-content-between ${styles.filterBox}`}>
                  <h5 className="card-title mb-0">
                    Cross Origin Domain Management
                  </h5>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="text"
                      placeholder="Search domain..."
                      className={`form-control mb-3 ${styles.customModalInput}`}
                      style={{
                        width: "220px",
                        fontSize: "14px",
                        color: "#000",
                      }}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button
                      className={`btn ${styles.btnInsertDomain}`}
                      onClick={() => setShowInsertPopup(true)}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Insert Domain
                    </button>
                  </div>
                </div>

                {/* Error Alert */}
                {error.show && (
                  <div className={error.bg} role="alert">
                    {error.message}
                  </div>
                )}

                {/* Domain Table */}
                <div className="table-responsive">
                  <table className="table table-striped table-sm align-middle">
                    <thead>
                      <tr>
                        <th>S.No.</th>
                        <th>Domain</th>
                        <th>Status</th>
                        <th>IP</th>
                        <th>Created On</th>
                        <th>Updated On</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentDomains.length > 0 ? (
                        currentDomains.map((item, index) => (
                          <tr key={item.id}>
                            <td>
                              {(currentPage - 1) * domainsPerPage + index + 1}
                            </td>
                            <td>{item.domain}</td>
                            <td>{item.status === 1 ? "Active" : "Inactive"}</td>
                            <td>{item.ip || "-"}</td>
                            <td>
                              {new Date(item.Created).toLocaleString() || "-"}
                            </td>
                            <td>
                              {new Date(item.Updated).toLocaleString() || "-"}
                            </td>
                            <td>
                              <button
                                onClick={() => {
                                  setSelectedDomain(item);
                                  setEditedDomain(item.domain);
                                  setEditedStatus(item.status ?? 1);
                                  setEditedIp(item.ip || "");
                                  setShowEditPopup(true);
                                }}
                                className="btn btn-sm btn-info me-3"
                              >
                                <FontAwesomeIcon icon={faEdit} /> Edit
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedDomain(item);
                                  setShowDeletePopup(true);
                                }}
                                className="btn btn-sm btn-danger"
                              >
                                <FontAwesomeIcon icon={faTrash} /> Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No domains found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {filteredDomains.length > domainsPerPage && (
                  <div className="d-flex justify-content-center align-items-center mt-3 gap-2">
                    <button
                      className="btn btn-outline-secondary btn-sm text-black"
                      style={{ color: "black" }}
                      onClick={prevPage}
                      disabled={currentPage === 1}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} color="black" /> Prev
                    </button>

                    <span className="mx-2" style={{ color: "black" }}>
                      Page {currentPage} of {totalPages}
                    </span>

                    <button
                      className="btn btn-outline-secondary btn-sm text-black"
                      style={{ color: "black" }}
                      onClick={nextPage}
                      disabled={currentPage === totalPages}
                    >
                      Next <FontAwesomeIcon icon={faChevronRight} color="black" />
                    </button>
                  </div>

                )}

                {/* Insert Popup */}
                {showInsertPopup && (
                  <div className={styles.customModalOverlay}>
                    <div className={styles.customModal}>
                      <h5 className="mb-3">Add New Domain</h5>
                      <input
                        type="text"
                        className={`form-control mb-3 ${styles.customModalInput}`}
                        placeholder="Enter domain name (e.g. example.com)"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                      />
                      <input
                        type="text"
                        className={`form-control mb-3 ${styles.customModalInput}`}
                        placeholder="Optional IP (e.g. 192.168.1.1)"
                        value={newIp}
                        onChange={(e) => setNewIp(e.target.value)}
                      />

                      <div className={styles.statusRow}>
                        <span className={styles.statusLabel}>Status:</span>

                        <label className={styles.switch}>
                          <input
                            type="checkbox"
                            checked={newStatus === 1}
                            onChange={(e) => setNewStatus(e.target.checked ? 1 : 0)}
                          />
                          <span className={styles.slider}></span>
                        </label>

                        <strong>{newStatus === 1 ? "Active" : "Inactive"}</strong>
                      </div>


                      <div className="d-flex justify-content-end gap-2">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            setShowInsertPopup(false);
                            setNewDomain("");
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-success btn-sm"
                          onClick={handleInsert}
                        >
                          Save Domain
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Edit Popup */}
                {showEditPopup && (
                  <div className={styles.customModalOverlay}>
                    <div className={styles.customModal}>
                      <h5 className="mb-3">Edit Domain</h5>
                      <input
                        type="text"
                        className={`form-control mb-3 ${styles.customModalInput}`}
                        placeholder="Enter updated domain name"
                        value={editedDomain}
                        onChange={(e) => setEditedDomain(e.target.value)}
                      />
                      <input
                        type="text"
                        className={`form-control mb-3 ${styles.customModalInput}`}
                        placeholder="Optional IP (e.g. 192.168.1.1)"
                        value={editedIp}
                        onChange={(e) => setEditedIp(e.target.value)}
                      />

                      <div className={styles.statusRow}>
                        <span className={styles.statusLabel}>Status:</span>

                        <label className={styles.switch}>
                          <input
                            type="checkbox"
                            checked={editedStatus === 1}
                            onChange={(e) => setEditedStatus(e.target.checked ? 1 : 0)}
                          />
                          <span className={styles.slider}></span>
                        </label>

                        <strong>{editedStatus === 1 ? "Active" : "Inactive"}</strong>
                      </div>
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setShowEditPopup(false)}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-success btn-sm"
                          onClick={handleEdit}
                        >
                          Update
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Delete Popup */}
                {showDeletePopup && (
                  <div className={styles.customModalOverlay}>
                    <div className={styles.customModal}>
                      <h5 className="mb-3 text-danger">Delete Domain</h5>
                      <p>
                        Are you sure you want to delete the domain{" "}
                        <strong>{selectedDomain?.domain}</strong>?
                      </p>
                      <div className="d-flex justify-content-end gap-2 mt-3">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setShowDeletePopup(false)}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={handleDelete}
                        >
                          Confirm Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CrossOrigin;